import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Loader2,
  CheckCircle2,
  Circle,
  Sparkles,
  Lightbulb,
  FileText,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  BookOpen
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import CollegeNotebook from '../CollegeNotebook/CollegeNotebook';

// Section Guidance Content
const SECTION_GUIDANCE = {
  hook: {
    brief: "Drop the reader into a specific moment with sensory details. Start mid-action, not with background or setup.",

    detailed: {
      title: "What Makes a Strong Opening Hook",
      content: `**What This Section Does:**
Drop the reader into a specific moment. Not background, not setup—action. The reader should be able to SEE something happening.

**What Works:**
- Start mid-scene with a sensory detail
- Use exact details: specific times, brand names, actual dialogue
- Show your state of mind through what you noticed, not what you felt
- One focused moment, not a summary of events

**What Doesn't Work:**
- "Ever since I was little..." (background, not a hook)
- "I've always been interested in..." (too generic)
- Starting with the outcome ("This experience taught me...")
- Vague emotions ("I was nervous") without showing what that looked like

**Example of Specificity:**
❌ "I was stuck in traffic and frustrated"
✅ "Spotify kept cutting out as the car inched forward, and the stop-and-go rhythm felt like torture"

**Your Goal:** Make the reader feel like they're standing next to you in this moment.`
    }
  },

  context: {
    brief: "Give just enough background to understand why this moment mattered. Focus on what made this situation uniquely challenging for you.",

    detailed: {
      title: "What Makes Strong Context/Background",
      content: `**What This Section Does:**
Give just enough background for the reader to understand why this moment matters. Not your life story—just the essential context for THIS situation.

**What Works:**
- What made this particular challenge different from others
- Why your usual approaches wouldn't work here
- The stakes: what would happen if you didn't figure this out
- Keep it brief (2-4 sentences max)

**What Doesn't Work:**
- Long history of your interest in the topic
- Listing accomplishments or credentials
- Abstract statements about who you are
- Information that doesn't directly connect to the opening moment

**Think About:**
- What did the reader just see in your opening?
- What do they need to know to understand what happens next?
- What was at stake for you specifically?

**Your Goal:** Connect the opening moment to why it mattered enough to change you.`
    }
  },

  rising_action: {
    brief: "Show your problem-solving process—the attempts, failures, and discoveries. This reveals how YOUR mind works differently than others.",

    detailed: {
      title: "What Makes Strong Rising Action",
      content: `**What This Section Does:**
Show your problem-solving process. This is where we see HOW your mind works—the attempts, the failures, the unexpected discoveries.

**What Works:**
- Walk through what you tried, in order
- Include what didn't work (the failures are interesting)
- Show your thinking process: "I realized..." "I noticed..." "I tried..."
- Be specific about your approach, not just the outcome
- Let us see the messy, real version

**What Doesn't Work:**
- Jumping straight to the solution
- "I worked really hard and eventually figured it out"
- Making it sound easy or inevitable
- Hiding the struggle or uncertainty
- Generic problem-solving language

**Your Approach Is Your Differentiator:**
Two students can face the same problem. What makes your essay unique is showing how YOUR brain tackled it differently than anyone else would.

**Your Goal:** Reveal your distinctive thinking process through specific actions.`
    }
  },

  turning_point: {
    brief: "Capture the specific moment something clicked—when you saw the problem differently or made an unexpected connection.",

    detailed: {
      title: "What Makes a Strong Turning Point",
      content: `**What This Section Does:**
Capture the moment something clicked—when you saw the problem differently, when a new approach emerged, when you had a realization.

**What Works:**
- A specific moment of insight, not gradual improvement
- What you suddenly understood that you hadn't before
- The connection or pattern you noticed
- How your perspective shifted
- One clear "aha" moment

**What Doesn't Work:**
- "After working hard, I succeeded"
- Vague growth claims ("I learned perseverance")
- Skipping over the actual insight
- Making it sound like a lesson you were taught vs. something you discovered
- Abstract realizations without concrete examples

**This Should Feel Like:**
The moment in your opening hook when "frustration crystallized into pattern recognition" or when you "caught yourself counting seconds between movements"

**Your Goal:** Show the exact moment when your understanding changed.`
    }
  },

  resolution: {
    brief: "Show what you did with your new understanding. Demonstrate the change through specific actions, not claims of success.",

    detailed: {
      title: "What Makes a Strong Resolution",
      content: `**What This Section Does:**
Show what you did with your new understanding. Not "I succeeded" but "here's how I applied this insight to solve the problem."

**What Works:**
- Describe what you did differently after your realization
- Show the new approach in action
- Include specific results or outcomes
- Connect back to the original challenge
- Demonstrate that the change was real, not just theoretical

**What Doesn't Work:**
- Generic success statements
- Claiming broad impact without specific evidence
- "Everyone was impressed" (focus on the action, not the praise)
- Listing achievements
- Skipping the actual application

**Evidence Matters:**
Don't just say you changed—show what you did that proves it. "I started sketching the lanes in my head" is evidence. "I became better at problem-solving" is not.

**Your Goal:** Prove the turning point was real by showing what changed in your actions.`
    }
  },

  closing: {
    brief: "Connect this experience to how you think now. Show one concrete example of where you've used this same approach since.",

    detailed: {
      title: "What Makes a Strong Closing",
      content: `**What This Section Does:**
Connect this specific experience to how you think now. Not "I learned X" but "here's how this changed my approach to other situations."

**What Works:**
- One concrete example of how this shows up in your life now
- Specific new situations where you've used this thinking
- Brief and forward-looking
- Shows transfer of learning, not just one-time success
- Your unique lens or framework

**What Doesn't Work:**
- Generic life lessons ("I learned to never give up")
- "This will help me in college" statements
- Repeating what you already said
- Broad claims about who you are now
- Making it sound like you're completely transformed

**Think About:**
- When did you catch yourself using this same approach again?
- What do you notice now that you didn't before?
- How has your default response changed?

**Strong Closing Pattern:**
"Six months later, when [new situation], I found myself automatically [applying the same thinking]. I realized I'd become someone who [new lens]."

**Your Goal:** Show this wasn't just about solving one problem—it's about how you approach problems now.`
    }
  }
};

/**
 * OutlineDraftDeveloper - AI-guided expansion of outline into full draft
 *
 * Takes an outline and helps students expand each section into 1-2 paragraphs
 * through targeted AI questioning and coaching
 */
export default function OutlineDraftDeveloper({ outline: initialOutline, onClose, onSave }) {
  // State for full outline with essay_idea data
  const [outline, setOutline] = useState(initialOutline);
  const [essayIdea, setEssayIdea] = useState(null);
  const [loadingEssayIdea, setLoadingEssayIdea] = useState(true);

  // Fetch essay idea with development responses on mount
  useEffect(() => {
    const fetchEssayIdea = async () => {
      if (!initialOutline.essay_idea_id) {
        setLoadingEssayIdea(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('college_journey_essay_ideas')
          .select('*')
          .eq('id', initialOutline.essay_idea_id)
          .single();

        if (error) throw error;

        console.log('📚 Loaded essay idea with development responses:', data);
        setEssayIdea(data);
      } catch (err) {
        console.error('Error loading essay idea:', err);
      } finally {
        setLoadingEssayIdea(false);
      }
    };

    fetchEssayIdea();
  }, [initialOutline.essay_idea_id]);

  // Define the 6 sections we'll work through
  const sections = [
    { key: 'hook', label: 'Opening Hook' },
    { key: 'context', label: 'Context/Background' },
    { key: 'rising_action', label: 'Rising Action' },
    { key: 'turning_point', label: 'Turning Point' },
    { key: 'resolution', label: 'Resolution' },
    { key: 'closing', label: 'Closing' }
  ];

  // State
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionResponses, setSectionResponses] = useState({});
  const [expansionPrompt, setExpansionPrompt] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saving' | 'saved' | 'error'
  const [scaffoldingLevel, setScaffoldingLevel] = useState('structural'); // 'full' | 'structural' | 'open'

  // Cache prompts by section and scaffolding level to avoid regeneration
  const [promptCache, setPromptCache] = useState({});

  // UI state for collapsible sections
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [showOutlineNotes, setShowOutlineNotes] = useState(false);
  const [scaffoldingDropdownOpen, setScaffoldingDropdownOpen] = useState(false);
  const [showSectionGuidance, setShowSectionGuidance] = useState(false);

  // Refs
  const textareaRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Load existing responses on mount (resume functionality)
  useEffect(() => {
    if (outline.development_responses) {
      console.log('📋 Loading existing draft responses...');
      setSectionResponses(outline.development_responses);

      // Find first incomplete section
      const firstIncompleteIndex = sections.findIndex(section => {
        const response = outline.development_responses[section.key];
        const wordCount = response ? response.trim().split(/\s+/).length : 0;
        return wordCount < (expansionPrompt?.min_words || 80);
      });

      if (firstIncompleteIndex !== -1) {
        setCurrentSectionIndex(firstIncompleteIndex);
        console.log(`📍 Resuming at ${sections[firstIncompleteIndex].label}`);
      } else {
        console.log('✅ All sections complete - starting at beginning for review');
      }
    }
  }, []);

  const currentSection = sections[currentSectionIndex];
  const outlineData = outline.outline_data || {};
  const currentOutlineNotes = outlineData[currentSection.key] || '';
  const currentResponse = sectionResponses[currentSection.key] || '';

  // Extract key details from prior responses
  const extractKeyDetails = (text) => {
    if (!text || text.trim().length < 50) return [];

    // Split into sentences
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

    // Extract quoted phrases (things in quotes are often vivid details)
    const quotedPhrases = text.match(/"([^"]+)"/g) || [];

    // Extract phrases with sensory/vivid language indicators
    const vividIndicators = /\b(looked like|sounded like|felt like|smelled like|tasted like|noticed|saw|heard|realized|thought|wondered)\s+([^.!?,]+)/gi;
    const vividPhrases = [];
    let match;
    while ((match = vividIndicators.exec(text)) !== null) {
      vividPhrases.push(match[0]);
    }

    // Combine and deduplicate
    const allDetails = [
      ...quotedPhrases.map(q => q.replace(/"/g, '\'')),
      ...vividPhrases.slice(0, 3), // Limit to top 3 vivid phrases
      ...sentences.slice(0, 2).map(s => s.trim()) // First 2 sentences as fallback
    ];

    // Return unique, non-empty details (max 5)
    return [...new Set(allDetails)]
      .filter(d => d && d.length > 10 && d.length < 150)
      .slice(0, 5);
  };

  // Load expansion prompt when section changes
  useEffect(() => {
    loadExpansionPrompt();
  }, [currentSectionIndex, scaffoldingLevel]);

  // Auto-focus textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentSectionIndex]);

  // Load AI expansion prompt for current section
  const loadExpansionPrompt = async () => {
    // Check cache first
    const cacheKey = `${currentSection.key}_${scaffoldingLevel}`;
    if (promptCache[cacheKey]) {
      console.log('✓ Using cached prompt for', cacheKey);
      setExpansionPrompt(promptCache[cacheKey]);
      return;
    }

    setLoadingPrompt(true);
    try {
      const response = await fetch('/api/outlines/generate-expansion-prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_key: currentSection.key,
          section_label: currentSection.label,
          outline_notes: currentOutlineNotes,
          strategic_angle: outline.strategic_angle || '',
          previous_sections: getPreviousSections(),
          user_id: outline.user_id,
          essay_idea_id: outline.essay_idea_id, // Pass essay_idea_id for contextual prompts
          scaffolding_level: scaffoldingLevel // Pass selected scaffolding level
        })
      });

      if (!response.ok) throw new Error('Failed to load prompt');

      const data = await response.json();
      setExpansionPrompt(data);

      // Cache the prompt
      setPromptCache(prev => ({
        ...prev,
        [cacheKey]: data
      }));
      console.log('✓ Cached prompt for', cacheKey);
    } catch (error) {
      console.error('Error loading expansion prompt:', error);
      // Fallback to generic prompt
      const fallbackPrompt = {
        prompt: `Expand on your outline notes for ${currentSection.label}. Write 1-2 paragraphs with specific details.`,
        coaching_hints: ['Add sensory details', 'Show, don\'t tell', 'Use specific examples'],
        min_words: 80
      };
      setExpansionPrompt(fallbackPrompt);

      // Cache the fallback too
      setPromptCache(prev => ({
        ...prev,
        [cacheKey]: fallbackPrompt
      }));
    } finally {
      setLoadingPrompt(false);
    }
  };

  // Get previously completed sections for context
  const getPreviousSections = () => {
    const previous = {};
    for (let i = 0; i < currentSectionIndex; i++) {
      const section = sections[i];
      if (sectionResponses[section.key]) {
        previous[section.key] = sectionResponses[section.key];
      }
    }
    return previous;
  };

  // Handle response change
  const handleResponseChange = (value) => {
    setSectionResponses(prev => ({
      ...prev,
      [currentSection.key]: value
    }));

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(200, textareaRef.current.scrollHeight)}px`;
    }

    // Debounced auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 2000);
  };

  // Auto-save progress
  const handleAutoSave = async () => {
    try {
      setAutoSaveStatus('saving');

      // Save section responses to outline record
      const { error } = await supabase
        .from('college_journey_essay_outlines')
        .update({
          development_responses: sectionResponses,
          updated_at: new Date().toISOString()
        })
        .eq('id', outline.id);

      if (error) throw error;

      setAutoSaveStatus('saved');
      console.log('✓ Auto-saved draft progress');
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('error');
    }
  };

  // Navigate to previous section
  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else if (onClose) {
      onClose();
    }
  };

  // Navigate to next section
  const handleContinue = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      toast.success(`Moving to ${sections[currentSectionIndex + 1].label}`);
    }
  };

  // Calculate word count for current section
  const wordCount = currentResponse.trim() ? currentResponse.trim().split(/\s+/).length : 0;
  const minWords = expansionPrompt?.min_words || 80;
  const isBelowRecommended = wordCount > 0 && wordCount < minWords;

  // Check if all sections have SOME content (not enforcing word count)
  const allSectionsComplete = sections.every(s =>
    sectionResponses[s.key] && sectionResponses[s.key].trim().length > 0
  );

  // Count sections below recommended word count
  const sectionsBelowRecommended = sections.filter(s => {
    const response = sectionResponses[s.key] || '';
    const wc = response.trim().split(/\s+/).length;
    return wc > 0 && wc < minWords;
  }).length;

  // Calculate total word count
  const totalWordCount = Object.values(sectionResponses)
    .filter(text => text)
    .join(' ')
    .trim()
    .split(/\s+/).length;

  // Finish and save as essay
  const handleFinishAndSave = async () => {
    setIsSaving(true);
    try {
      // Compile all sections into essay content
      const compiledDraft = sections
        .map(s => sectionResponses[s.key] || '')
        .filter(text => text.length > 0)
        .join('\n\n');

      // Call parent save handler
      await onSave({
        content: compiledDraft,
        sections: sectionResponses,
        wordCount: totalWordCount
      });

      toast.success('Draft completed!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  // Get prior response and extracted details for current section
  const getPriorResponseData = () => {
    if (!essayIdea?.development_responses) return { priorResponse: null, keyDetails: [] };

    // Flatten all responses from all stages
    const allResponses = {};
    Object.values(essayIdea.development_responses).forEach(stage => {
      if (stage.responses) {
        Object.assign(allResponses, stage.responses);
      }
    });

    // Map sections to likely question IDs
    const sectionKeyMappings = {
      'hook': ['moment_of_realization', 'opening_moment', 'hook_detail', 'hook'],
      'context': ['behind_scenes_observation', 'context_detail', 'background', 'context'],
      'rising_action': ['systematic_thinking_surprise', 'rising_action_detail', 'development', 'rising_action'],
      'turning_point': ['teacher_section_discovery', 'turning_point_detail', 'climax', 'turning_point'],
      'resolution': ['philosophical_implications', 'resolution_detail', 'outcome', 'resolution'],
      'closing': ['assistant_principal_conversation', 'closing_detail', 'reflection', 'closing']
    };

    const possibleKeys = sectionKeyMappings[currentSection.key] || [currentSection.key];
    for (const key of possibleKeys) {
      if (allResponses[key] && allResponses[key].trim()) {
        const priorResponse = allResponses[key];
        const keyDetails = extractKeyDetails(priorResponse);
        return { priorResponse, keyDetails };
      }
    }

    return { priorResponse: null, keyDetails: [] };
  };

  const { priorResponse, keyDetails } = getPriorResponseData();
  const priorResponsePreview = priorResponse ? priorResponse.substring(0, 150) + '...' : '';

  // Scaffolding content labels
  const scaffoldingLabels = {
    full: {
      label: 'Full Scaffolding',
      description: 'Step-by-step sentence starters'
    },
    structural: {
      label: 'Structural Prompts',
      description: 'Paragraph structure guidance'
    },
    open: {
      label: 'Open Writing',
      description: 'Minimal support for confident writers'
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-50 z-50 flex flex-col">
      {/* College Notebook - show for supplemental essays with college_id */}
      {essayIdea?.essay_type === 'supplemental' && essayIdea?.college_id && (
        <CollegeNotebook
          collegeId={essayIdea.college_id}
          essaySlotId={essayIdea.essay_slot_id}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b-2 border-slate-900 px-6 py-4 flex-shrink-0">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-stone-100 rounded transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Develop Draft</h1>
              <p className="text-xs text-slate-600">{outline.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Auto-save indicator */}
            <div className="flex items-center gap-2 text-xs text-slate-600">
              {autoSaveStatus === 'saving' && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  <span>Saving...</span>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Auto-saved</span>
                </>
              )}
              {autoSaveStatus === 'error' && (
                <>
                  <Circle className="w-4 h-4 text-red-500" />
                  <span className="text-red-600">Save failed</span>
                </>
              )}
            </div>

            {/* Save & Exit button */}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2 text-sm border-2 border-slate-900"
            >
              <CheckCircle2 className="w-4 h-4" />
              Save & Exit
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Three Column Layout */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-[1600px] mx-auto h-full flex">

          {/* Left Column - Reference Panel (400px, Scrollable) */}
          <div className="w-[400px] border-r border-stone-300 bg-white overflow-y-auto flex-shrink-0">
            <div className="p-6 space-y-4">

              {/* Section Header */}
              <div>
                <span className="text-xs font-bold text-violet-600 uppercase tracking-wider">
                  Section {currentSectionIndex + 1} of {sections.length}
                </span>
                <h2 className="text-xl font-bold text-slate-900 mt-1">{currentSection.label}</h2>
                <div className="flex items-center gap-4 text-xs mt-2">
                  <span className="text-slate-600">Target: {minWords}+ words</span>
                  <span className={`font-bold ${wordCount >= minWords ? 'text-emerald-600' : 'text-slate-900'}`}>
                    Current: {wordCount}
                  </span>
                </div>
              </div>

              {/* Outline Notes - Collapsible */}
              {currentOutlineNotes && (
                <div className="border border-stone-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowOutlineNotes(!showOutlineNotes)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-3 h-3 text-slate-600" />
                      <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                        Outline Notes
                      </span>
                    </div>
                    {showOutlineNotes ? (
                      <ChevronUp className="w-3 h-3 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-slate-600" />
                    )}
                  </button>
                  {showOutlineNotes && (
                    <div className="px-3 pb-3 pt-2 border-t border-stone-200">
                      <p className="text-xs text-slate-700 leading-relaxed">
                        {currentOutlineNotes}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Prior Response - Collapsible */}
              {priorResponse && !loadingEssayIdea && (
                <div className="bg-violet-50 border border-violet-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowFullResponse(!showFullResponse)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-violet-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-violet-600 rounded-full flex-shrink-0"></div>
                      <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                        You Already Wrote This
                      </span>
                    </div>
                    {showFullResponse ? (
                      <Minimize2 className="w-4 h-4 text-violet-600" />
                    ) : (
                      <Maximize2 className="w-4 h-4 text-violet-600" />
                    )}
                  </button>
                  <div className="px-4 pb-4">
                    <p className="text-xs text-slate-700 leading-relaxed">
                      {showFullResponse ? priorResponse : priorResponsePreview}
                    </p>
                    {!showFullResponse && (
                      <button
                        onClick={() => setShowFullResponse(true)}
                        className="text-xs text-violet-600 font-medium mt-2 hover:text-violet-700"
                      >
                        Read full response →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Tips */}
              {expansionPrompt?.coaching_hints && expansionPrompt.coaching_hints.length > 0 && (
                <div className="border border-stone-300 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-3 h-3 text-slate-600" />
                    <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Quick Tips
                    </span>
                  </div>
                  <div className="space-y-1">
                    {expansionPrompt.coaching_hints.map((hint, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1 h-1 bg-slate-900 rounded-full mt-1 flex-shrink-0"></div>
                        <span className="text-xs text-slate-600">{hint}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Column - Writing Area (Fixed, Always Visible) */}
          <div className="flex-1 flex flex-col bg-stone-50 overflow-hidden">
            <div className="flex-1 flex flex-col p-8 overflow-y-auto">

              {/* Section Guidance */}
              <div className="pb-4">
                <label className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex-shrink-0 block">
                  Write 1-2 Paragraphs
                </label>

                {/* Section Guidance - Brief + Expandable */}
                {SECTION_GUIDANCE[currentSection.key] && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-slate-800 leading-relaxed mb-2">
                      {SECTION_GUIDANCE[currentSection.key].brief}
                    </p>
                    <button
                      onClick={() => setShowSectionGuidance(!showSectionGuidance)}
                      className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 transition-colors"
                    >
                      <BookOpen className="w-3 h-3" />
                      {showSectionGuidance ? 'Hide detailed guidance' : 'Show examples & tips'}
                      <ChevronDown className={`w-3 h-3 transition-transform ${showSectionGuidance ? 'rotate-180' : ''}`} />
                    </button>

                    {showSectionGuidance && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <h4 className="text-xs font-bold text-blue-900 mb-2">
                          {SECTION_GUIDANCE[currentSection.key].detailed.title}
                        </h4>
                        <div className="text-xs text-slate-700 leading-relaxed space-y-2">
                          {SECTION_GUIDANCE[currentSection.key].detailed.content.split('\n\n').map((paragraph, idx) => (
                            <p key={idx} className="whitespace-pre-wrap">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Key Details - Compact */}
              {keyDetails.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-2">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2">
                    Details to Include
                  </h4>
                  <div className="space-y-1">
                    {keyDetails.map((detail, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1 h-1 bg-violet-600 rounded-full mt-1 flex-shrink-0"></div>
                        <span className="text-xs text-slate-700">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
                <textarea
                  ref={textareaRef}
                  value={currentResponse}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  placeholder="Start expanding your response here..."
                  className="flex-1 w-full px-6 py-4 border-2 border-stone-300 rounded-lg focus:border-violet-600 focus:outline-none text-base text-slate-900 leading-relaxed resize-none bg-white"
                  style={{ minHeight: '500px' }}
                />
                <div className="mt-3 flex items-center justify-between text-xs flex-shrink-0">
                  <span className="text-slate-600">
                    Press Ctrl+Enter to continue
                  </span>
                  {wordCount > 0 && wordCount < minWords && (
                    <span className="text-amber-600 font-medium">
                      {minWords - wordCount} more words needed
                    </span>
                  )}
                  {wordCount >= minWords && (
                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Ready to continue
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Navigation - Fixed */}
            <div className="border-t border-stone-300 bg-white px-8 py-4 flex items-center justify-between flex-shrink-0">
              <button
                onClick={handleBack}
                className="px-4 py-2 text-slate-700 hover:bg-stone-100 border border-stone-300 rounded transition-colors font-medium flex items-center gap-2 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {currentSectionIndex === 0 ? 'Cancel' : 'Previous'}
              </button>

              {currentSectionIndex === sections.length - 1 ? (
                <div className="flex flex-col items-end gap-1">
                  {sectionsBelowRecommended > 0 && !isSaving && (
                    <span className="text-xs text-amber-600 font-medium">
                      {sectionsBelowRecommended} {sectionsBelowRecommended === 1 ? 'section' : 'sections'} below recommended length
                    </span>
                  )}
                  <button
                    onClick={handleFinishAndSave}
                    disabled={!allSectionsComplete || isSaving}
                    className="px-6 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-slate-900 text-sm"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Finish & Save Draft
                        <CheckCircle2 className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-end gap-1">
                  {isBelowRecommended && (
                    <span className="text-xs text-amber-600 font-medium">
                      {minWords - wordCount} more words recommended
                    </span>
                  )}
                  <button
                    onClick={handleContinue}
                    className="px-6 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors font-medium flex items-center gap-2 border-2 border-slate-900 text-sm"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Progress & Preview */}
          <div className="w-[320px] border-l border-stone-300 bg-white overflow-y-auto flex-shrink-0">
            <div className="p-6 space-y-6">

              {/* Scaffolding Level Selector */}
              <div className="border-2 border-slate-900 rounded-lg overflow-hidden">
                <div className="bg-slate-900 px-4 py-2">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                    Writing Support
                  </h3>
                </div>
                <div className="p-4">
                  {/* Custom Dropdown */}
                  <div className="mb-4 relative">
                    <button
                      onClick={() => setScaffoldingDropdownOpen(!scaffoldingDropdownOpen)}
                      className="w-full text-left p-2 rounded bg-violet-50 border border-violet-600 hover:bg-violet-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 rounded-full flex-shrink-0 bg-violet-600"></div>
                            <span className="text-xs font-bold text-violet-600">
                              {scaffoldingLabels[scaffoldingLevel].label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 ml-4">
                            {scaffoldingLabels[scaffoldingLevel].description}
                          </p>
                        </div>
                        {scaffoldingDropdownOpen ? (
                          <ChevronUp className="w-4 h-4 text-slate-600 ml-2 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-600 ml-2 flex-shrink-0" />
                        )}
                      </div>
                    </button>

                    {/* Dropdown Menu */}
                    {scaffoldingDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-300 rounded-lg shadow-lg z-10">
                        {Object.entries(scaffoldingLabels).map(([key, data]) => (
                          <button
                            key={key}
                            onClick={() => {
                              setScaffoldingLevel(key);
                              setScaffoldingDropdownOpen(false);
                            }}
                            className={`w-full text-left p-2 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              scaffoldingLevel === key
                                ? 'bg-violet-50'
                                : 'hover:bg-stone-50'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                scaffoldingLevel === key ? 'bg-violet-600' : 'bg-stone-300'
                              }`}></div>
                              <span className={`text-xs font-bold ${
                                scaffoldingLevel === key ? 'text-violet-600' : 'text-slate-900'
                              }`}>
                                {data.label}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 ml-4">
                              {data.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Level Content - Shows AI-generated prompt content */}
                  {loadingPrompt ? (
                    <div className="pt-4 border-t border-stone-200 flex items-center gap-2 text-slate-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-xs">Loading guidance...</span>
                    </div>
                  ) : expansionPrompt && (
                    <div className="pt-4 border-t border-stone-200">
                      {/* Format the prompt with better structure for paragraph/bullet guidance */}
                      {(() => {
                        const promptText = expansionPrompt.prompt;

                        // Split on bullet points or numbered lists
                        const parts = promptText.split(/(?=•|\d+\.)/);

                        if (parts.length > 1) {
                          // Has structured content (bullets or numbered lists)
                          return (
                            <div className="space-y-3">
                              {parts.map((part, idx) => {
                                const trimmed = part.trim();
                                if (!trimmed) return null;

                                // Check if it's a bullet or numbered item
                                const isBullet = trimmed.startsWith('•') || /^\d+\./.test(trimmed);

                                if (isBullet) {
                                  return (
                                    <div key={idx} className="flex items-start gap-2">
                                      <span className="text-violet-600 font-bold text-xs mt-0.5">
                                        {trimmed.match(/^[•\d.]+/)?.[0] || '•'}
                                      </span>
                                      <p className="text-xs text-slate-700 leading-relaxed flex-1">
                                        {trimmed.replace(/^[•\d.]+\s*/, '')}
                                      </p>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <p key={idx} className="text-xs text-slate-700 leading-relaxed">
                                      {trimmed}
                                    </p>
                                  );
                                }
                              })}
                            </div>
                          );
                        } else {
                          // No structured content, just display as is with paragraph breaks
                          return (
                            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
                              {promptText}
                            </p>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Progress */}
              <div className="border border-slate-900 rounded-lg p-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">
                  Progress
                </h3>
                <div className="space-y-2">
                  {sections.map((section, index) => {
                    const isComplete = sectionResponses[section.key] &&
                      sectionResponses[section.key].trim().split(/\s+/).length >= minWords;
                    const isCurrent = index === currentSectionIndex;

                    return (
                      <button
                        key={section.key}
                        onClick={() => setCurrentSectionIndex(index)}
                        className={`w-full flex items-center gap-2 text-xs p-2 rounded text-left transition-colors ${
                          isCurrent ? 'bg-violet-50 text-violet-600 font-medium' :
                          isComplete ? 'text-emerald-600 hover:bg-emerald-50' :
                          'text-slate-400 hover:bg-stone-50'
                        }`}
                      >
                        {isComplete ? (
                          <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                        ) : (
                          <Circle className="w-3 h-3 flex-shrink-0" />
                        )}
                        <span>{section.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <div className="text-xs text-slate-600">
                    Total: <span className="font-bold text-slate-900">{totalWordCount} words</span>
                  </div>
                </div>
              </div>

              {/* Draft Preview */}
              <div className="bg-stone-100 border border-stone-300 rounded-lg p-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3">
                  Live Preview
                </h3>
                {currentResponse.trim() ? (
                  <div>
                    <p className="text-xs font-bold text-slate-500 mb-1">{currentSection.label}</p>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{currentResponse}</p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">
                    Your draft will appear here as you write...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
